import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Snackbar,
  Alert,
  IconButton,
  FormControl,
  FormHelperText,
} from "@mui/material";
import { CheckBox, CheckBoxOutlineBlank } from "@mui/icons-material";
import Papa from "papaparse";
import axios from "axios";
import { OrderContext } from "../../contexts/OrderContext";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";
let IMAGE_BASE_URL = "";

const EditFilter = ({ open, onClose, filter }) => {
  const [tagNumbers, setTagNumbers] = useState([]);
  const [stockData, setStockData] = useState([]);
  const [error, setError] = useState(null);
  const [selectedTagNos, setSelectedTagNos] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [filterName, setFilterName] = useState("");
  const [expiryDate, setExpiryDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const selectedManufacturer = JSON.parse(localStorage.getItem("selectedMfr"));
  const mfrId = selectedManufacturer?.id;

  const { storeData } = useContext(OrderContext);
  const { storeInfo = [] } = storeData;

  if (Array.isArray(storeInfo) && storeInfo.length > 0) {
    const { mail_id, url_prefix } = storeInfo[0];
    IMAGE_BASE_URL = `https://kumuduorderapp.blob.core.windows.net/${url_prefix}/`;
  } else {
    console.log("No store information found.");
  }

  useEffect(() => {
    if (filter) {
      setFilterName(filter.name || "");
      setExpiryDate(
        filter.expiryDate
          ? filter.expiryDate.split("T")[0]
          : new Date().toISOString().split("T")[0]
      );

      try {
        const savedTagNos =
          filter.selectedStock && typeof filter.selectedStock === "string"
            ? JSON.parse(filter.selectedStock)
            : [];

        setSelectedTagNos(savedTagNos);
      } catch (error) {
        console.error(
          "Error parsing selectedStock:",
          error,
          filter.selectedStock
        );
        setSelectedTagNos([]);
      }
    }
  }, [filter]);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];

    if (file) {
      Papa.parse(file, {
        complete: (result) => {
          const extractedTagNumbers = result.data
            .map((row) => row[0])
            .filter(Boolean);
          setTagNumbers(extractedTagNumbers);
        },
        skipEmptyLines: true,
      });
    }
  };

  const fetchStockDetails = async () => {
    if (tagNumbers.length === 0) {
      setError("No tag numbers found. Please upload a valid CSV.");
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/fetchorder/getStockByTags`,
        {
          tagNumbers,
          store_id: mfrId,
        }
      );

      const fetchedStockData = response.data;

      let savedTagNos = [];
      try {
        savedTagNos =
          filter.selectedStock && typeof filter.selectedStock === "string"
            ? JSON.parse(filter.selectedStock)
            : filter.selectedStock || [];
      } catch (error) {
        console.error("Error parsing selectedStock:", error);
        savedTagNos = [];
      }

      const preselectedTags = fetchedStockData
        .filter((stock) => savedTagNos.includes(stock.tagno))
        .map((stock) => stock.tagno);

      setStockData(fetchedStockData);
      setSelectedTagNos(preselectedTags);
      setSelectAll(preselectedTags.length === fetchedStockData.length);
      setError(null);
    } catch (error) {
      console.error("Error fetching stock data:", error);
      setError("Error fetching stock data.");
    }
  };

  const handleRowSelection = (tagno) => {
    setSelectedTagNos((prevSelected) =>
      prevSelected.includes(tagno)
        ? prevSelected.filter((id) => id !== tagno)
        : [...prevSelected, tagno]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedTagNos([]);
    } else {
      setSelectedTagNos(stockData.map((stock) => stock.tagno));
    }
    setSelectAll(!selectAll);
  };

  const updateFilter = async () => {
    if (selectedTagNos.length === 0) {
      showSnackbar("No stocks selected!", "error");
      return;
    }

    if (!filterName.trim()) {
      showSnackbar("Please enter a filter name.", "error");
      return;
    }

    const filterData = {
      filterId: filter.filterId,
      name: filterName,
      selectedStock: JSON.stringify(selectedTagNos),
      expiryDate: expiryDate,
      store_id: mfrId,
    };

    try {
      await axios.put(
        `${API_BASE_URL}/api/fetchorder/updateFilterTags/${filter.filterId}`,
        filterData
      );
      showSnackbar("Filter updated successfully!", "success");
      onClose();
    } catch (error) {
      console.error("Error updating filter:", error);
      showSnackbar("Failed to update filter.", "error");
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen fullWidth maxWidth="xl">
      <DialogTitle>Edit Filter</DialogTitle>
      <DialogContent>
        <TextField
          label="Filter Name"
          variant="outlined"
          fullWidth
          sx={{ mt: 2 }}
          value={filterName}
          onChange={(e) => setFilterName(e.target.value)}
          required
        />

        <TextField
          label="Expiry Date"
          type="date"
          variant="outlined"
          fullWidth
          sx={{ mt: 2 }}
          value={expiryDate}
          onChange={(e) => setExpiryDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          required
        />

        <FormControl fullWidth sx={{ mt: 2 }}>
          <Button variant="contained" component="label">
            Upload CSV File
            <input
              type="file"
              accept=".csv"
              hidden
              onChange={handleFileUpload}
            />
          </Button>
          <FormHelperText>
            CSV file should contain tag numbers in the first column
          </FormHelperText>
        </FormControl>

        {tagNumbers.length > 0 && (
          <TextField
            label="Extracted Tag Numbers"
            multiline
            fullWidth
            rows={4}
            value={tagNumbers.join(", ")}
            variant="outlined"
            margin="normal"
            sx={{ mt: 2 }}
          />
        )}

        {tagNumbers.length > 0 && (
          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
            onClick={fetchStockDetails}
          >
            Fetch Stock Details
          </Button>
        )}

        {error && (
          <Typography color="error" textAlign="center" mt={2}>
            {error}
          </Typography>
        )}

        {stockData.length > 0 && (
          <>
            <FormControl sx={{ mt: 2 }}>
              <Button
                startIcon={
                  selectAll ? (
                    <CheckBox color="primary" />
                  ) : (
                    <CheckBoxOutlineBlank />
                  )
                }
                onClick={handleSelectAll}
                sx={{ textTransform: "none" }}
              >
                Select All
              </Button>
            </FormControl>

            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Select</TableCell>
                    <TableCell>Tag No</TableCell>
                    <TableCell>Item Type</TableCell>
                    <TableCell>Design</TableCell>
                    <TableCell>Metal Type</TableCell>
                    <TableCell>Purity</TableCell>
                    <TableCell>Gross Weight</TableCell>
                    <TableCell>Image</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockData.map((stock, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <IconButton
                          onClick={() => handleRowSelection(stock.tagno)}
                        >
                          {selectedTagNos.includes(stock.tagno) ? (
                            <CheckBox color="primary" />
                          ) : (
                            <CheckBoxOutlineBlank />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>{stock.tagno}</TableCell>
                      <TableCell>{stock.itemtype_name}</TableCell>
                      <TableCell>{stock.design_name}</TableCell>
                      <TableCell>{stock.purity_name}</TableCell>
                      <TableCell>{stock.purity}</TableCell>
                      <TableCell>{stock.gross}</TableCell>
                      <TableCell>
                        <img
                          src={`${IMAGE_BASE_URL}${stock.tagno_alpha || stock.tagno}.jpg`}
                          alt="Stock Item"
                          width="50"
                          height="50"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={updateFilter}
          disabled={selectedTagNos.length === 0}
        >
          Save Catalogue
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{
          '&.MuiSnackbar-root': {
            bottom: '100px !important', // force override snackbar position
          }
        }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default EditFilter;
